import { useEffect, useRef, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, ExternalLink, FileText, Upload, Trash2 } from "lucide-react";
import { uploadFile, apiError, downloadSigned } from "@/lib/api";
import { fmtQty, fmtDate, kb, domain, orderState } from "./helpers";
import { Link } from "react-router-dom";
import { DecorPickerList } from "./DecorPickerList";
import { CategorySelect } from "./CategorySelect";

const OK = ["application/pdf", "image/jpeg", "image/png"];
const okFile = (f) => OK.includes(f.type) || /\.(pdf|jpe?g|png)$/i.test(f.name || "");

// Panel detailu položky (§7). Desktop pravý panel, mobil spodní sheet.
export function ItemDrawer({ item, cls, group, decors, cats, canEdit, canVzornik, decorById,
  onClose, onSaveField, onPickDecor, onPickCustom, onClearCustom, onManageCats, onOrder, onCancelOrder, onAddOrderFile, onDeleteOrderFile, onRemove }) {
  const [desktop, setDesktop] = useState(typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches);
  const [label, setLabel] = useState("");
  const [qty, setQty] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const on = (e) => setDesktop(e.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  useEffect(() => {
    if (!item) return;
    setLabel(item.label || ""); setQty(fmtQty(item.qty)); setNote(item.note || "");
  }, [item?.id]); // eslint-disable-line

  if (!item) return null;
  const decor = decorById[item.dekor_id];
  const hasDecor = !!(item.dekor_id || item.dekor_text);
  // Kategorie zúží nabídku dekorů natvrdo (select má vždy platnou hodnotu).
  const scopeCat = (cats || []).find((c) => c.name.toLowerCase() === (item.category || "").trim().toLowerCase())?.name || null;

  const addFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!okFile(f)) { toast.error("Přílohu lze přiložit jen jako PDF, JPG nebo PNG."); return; }
    if (f.size > 15 * 1024 * 1024) { toast.error("Příloha je příliš velká (max 15 MB)."); return; }
    setBusy(true);
    try { const url = await uploadFile(f); await onAddOrderFile(item, { file_url: url, name: f.name, size: f.size }); }
    catch (e2) { toast.error(apiError(e2)); }
    finally { setBusy(false); }
  };

  return (
    <Sheet open={!!item} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side={desktop ? "right" : "bottom"}
        className={desktop ? "w-[440px] sm:max-w-[440px] overflow-y-auto" : "rounded-t-[24px] max-h-[92%] overflow-y-auto pb-[env(safe-area-inset-bottom)]"}
        data-testid="pasport-item-drawer">
        <SheetHeader>
          <div className="text-[11px] font-bold tracking-[.1em] text-muted-foreground uppercase">{cls?.name} · {group}</div>
          <SheetTitle className="font-heading font-extrabold text-[22px] leading-tight">{item.label}</SheetTitle>
          <SheetDescription className="sr-only">Detail položky pasportu</SheetDescription>
        </SheetHeader>

        <div className="space-y-4 mt-3">
          {/* 1. stavový blok */}
          {item.order?.order_id ? (
            <div className="rounded-[12px] px-3.5 py-3 bg-success/10 border border-success/30 flex items-center gap-2" data-testid="drawer-linked-order">
              <span className="flex-1 min-w-0">
                <span className="block text-success font-bold text-[14px]"><Check size={15} className="inline mr-1" />{orderState(item.order).title}</span>
                <span className="block text-[12.5px] text-muted-foreground">{orderState(item.order).sub}</span>
              </span>
              {canEdit && <Link to={`/objednavky?o=${item.order.order_id}`} className="text-brand text-[13px] font-bold hover:underline shrink-0" data-testid="drawer-open-order">Otevřít objednávku</Link>}
            </div>
          ) : item.order ? (
            <div className="rounded-[12px] px-3.5 py-3 bg-success/10 border border-success/30 flex items-center gap-2">
              <span className="text-success font-bold text-[14px] flex-1"><Check size={15} className="inline mr-1" />Objednáno {fmtDate(item.order.date)} · č. obj. {item.order.no}</span>
              {canEdit && <button type="button" onClick={() => onCancelOrder(item)} className="text-brand text-[13px] font-bold hover:underline" data-testid="drawer-cancel-order">Zrušit objednávku</button>}
            </div>
          ) : hasDecor ? (
            <div className="rounded-[12px] px-3.5 py-3 bg-brand/15 border border-brand/30">
              <div className="flex items-center gap-2">
                <span className="text-[14px] font-semibold flex-1">Produkt vybraný, čeká na objednání</span>
                {canEdit && <Button onClick={() => onOrder(item)} className="h-9 font-bold" data-testid="drawer-order">Objednat</Button>}
              </div>
              {item.dekor_url && (
                <a href={item.dekor_url} target="_blank" rel="noreferrer" className="mt-1.5 inline-flex items-center gap-1 text-[13px] font-bold text-brand" data-testid="drawer-custom-link">
                  <ExternalLink size={13} /> {item.dekor_text} · {domain(item.dekor_url)}
                </a>
              )}
            </div>
          ) : (
            <div className="rounded-[12px] px-3.5 py-3 bg-amber-500/10 border border-amber-500/30 text-[14px] font-semibold text-amber-700 dark:text-amber-400">
              Chybí dekor nebo odkaz — bez nich nelze objednat
            </div>
          )}

          {/* 2. doklady */}
          {item.order && !item.order.order_id && (
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[.08em] text-muted-foreground mb-2">Doklady k objednávce</div>
              <div className="space-y-1.5">
                {item.order.url && (
                  <a href={item.order.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[13px] font-bold text-brand">
                    <ExternalLink size={14} /> Objednávka u dodavatele
                  </a>
                )}
                {(item.order.files || []).map((f) => (
                  <div key={f.id} className="flex items-center gap-2 text-[13px] rounded-[10px] border border-border px-2.5 py-2" data-testid={`drawer-file-${f.id}`}>
                    <FileText size={14} className="text-muted-foreground shrink-0" />
                    <span className="flex-1 truncate">{f.name} <span className="text-muted-foreground">· {kb(f.size)}</span></span>
                    <a href={fileHref(f.url)} onClick={(e) => { e.preventDefault(); downloadSigned(f.url, f.name); }} className="text-brand font-bold cursor-pointer">stáhnout</a>
                    {canEdit && <button type="button" onClick={() => onDeleteOrderFile(item, f.id)} className="text-muted-foreground hover:text-destructive" data-testid={`drawer-file-del-${f.id}`}><Trash2 size={13} /></button>}
                  </div>
                ))}
                {canEdit && (
                  <label className={`flex items-center justify-center gap-2 h-10 rounded-[10px] border border-dashed border-border cursor-pointer text-[13px] font-semibold text-muted-foreground hover:bg-secondary ${busy ? "opacity-60 pointer-events-none" : ""}`} data-testid="drawer-add-file">
                    <Upload size={14} /> {busy ? "Nahrávám…" : "Přidat další soubor"}
                    <input ref={fileRef} type="file" accept=".pdf,image/jpeg,image/png,application/pdf" className="hidden" onChange={addFile} />
                  </label>
                )}
              </div>
            </div>
          )}

          {/* 3-7 pole */}
          <Field label="Co se řeší">
            <Input value={label} onChange={(e) => setLabel(e.target.value)} disabled={!canEdit}
              onBlur={() => label.trim() && label !== item.label && onSaveField(item.id, { label: label.trim() })}
              className="h-11" data-testid="drawer-label" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Místnost"><div className="h-11 flex items-center px-3 rounded-[10px] bg-muted text-sm text-muted-foreground">{group}</div></Field>
            <Field label="Množství">
              <Input value={qty} onChange={(e) => setQty(e.target.value)} disabled={!canEdit} inputMode="decimal"
                onBlur={() => onSaveField(item.id, { qty: qty === "" ? 1 : qty })} className="h-11 text-right tabular-nums" data-testid="drawer-qty" />
            </Field>
          </div>
          <Field label="Kategorie">
            <CategorySelect value={item.category || ""} cats={cats} canVzornik={canVzornik} onManage={onManageCats}
              onChange={(v) => onSaveField(item.id, { category: v })}
              className="h-11" testid="drawer-category" placeholder="Vyberte kategorii" />
          </Field>

          {canEdit && (
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[.08em] text-muted-foreground mb-2">Dekor / vlastní odkaz</div>
              <div className="border border-border rounded-[12px] overflow-hidden">
                <DecorPickerList decors={decors} value={item.dekor_id}
                  onPick={(id) => onPickDecor(item.id, item.dekor_id === id ? null : id)}
                  preferredCategory={scopeCat}
                  onPickCustom={(c) => onPickCustom(item.id, c)}
                  customCurrent={!item.dekor_id && item.dekor_text ? item.dekor_text : null}
                  onClearCustom={!item.dekor_id && item.dekor_text ? () => onClearCustom(item.id) : undefined} />
              </div>
            </div>
          )}

          <Field label="Poznámka">
            <textarea value={note} onChange={(e) => setNote(e.target.value)} disabled={!canEdit} rows={3}
              onBlur={() => (note || "") !== (item.note || "") && onSaveField(item.id, { note })}
              className="w-full min-h-[76px] rounded-[10px] border border-border bg-card px-3 py-2 text-sm resize-y" data-testid="drawer-note" />
          </Field>

          {canEdit && (
            <div className="pt-2 border-t border-border">
              <p className="text-[12px] text-muted-foreground mb-2">
                Změny se ukládají hned. Odkaz a dodavatel se berou ze vzorníku{decor ? ` (${decor.supplier})` : ""}.
              </p>
              <button type="button" onClick={() => onRemove(item)} className="text-[13px] font-bold text-destructive inline-flex items-center gap-1.5" data-testid="drawer-remove">
                <Trash2 size={14} /> Smazat položku
              </button>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="text-[11px] font-bold uppercase tracking-[.08em] text-muted-foreground">{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);

const fileHref = (u) => {
  const be = process.env.REACT_APP_BACKEND_URL || "";
  return u?.startsWith("/api/") ? `${be}${u}` : u;
};
